import * as PlansApi from '@client-shared/api/plans.api.js'
import filterFunctions from '@client-shared/utils/filter-functions.js'
import ignoreCanceledRequest from '@client-shared/utils/ignore-canceled-request.js'

import constants from '@/config/constants.js'

const fetchAbortControllers = {}

export default {
  namespaced: true,

  state: () => {
    return {
      list: [],
      // Project id this store's `list` was last fetched for. Lets the socket
      // reconnect catch-up skip stores that have never been loaded for the
      // current project (see cliesrc/utils/refetch-project-data.js).
      loadedProjectId: null,
      markedItem: null,
      selectedMultiActionProperty: constants.MULTI_ACTION.TOP_CATEGORY,

      sortSettings: {
        sortProperty: constants.SORT_PROPERTIES.ITEM_NAME,
        sortOrderReversed: false,
      },

      printSettings: {
        groupBy: constants.ITEM_PRINT_SETTINGS.GROUP_BY_NONE,
        showItemUrl: false,
        showComments: false,
      },
    }
  },

  getters: {
    listCountUntrashed: (state) => {
      return state.list
        .filter(p => !p.trashed)
        .length
    },

    getListMap: (state) => {
      return new Map(state.list.map(plan => [
        plan._id,
        plan,
      ]))
    },

    getById: (state, getters) => {
      return planId => getters.getListMap.get(planId)
    },

    getByName: (state) => {
      return planName => { return state.list.filter(plan => plan.name === planName) }
    },

    trashedItems: (state) => {
      return state.list.filter(item => item.trashed)
    },

    notTrashedItems: (state) => {
      return state.list.filter(item => !item.trashed)
    },

    listFilteredByModus: (state, getters, rootState, rootGetters) => {
      const modus = rootState.plansListModus.modus

      if (modus === constants.LIST_MODUS.APPROVAL) {
        const plansNotTrashed = state.list.filter(p => !p.trashed)
        const project = rootState.project.project
        const currentUserParticipant = project.findParticipantByUserId(rootState.currentUser.user._id)

        if (!currentUserParticipant) {
          return plansNotTrashed
        }

        return plansNotTrashed.filter(p => {
          const planApproval = p.computed.latestRevision.approval

          if (!planApproval || !planApproval.by) {
            return false
          }

          const approver = p.computed.latestRevision.approval.by.find(by => by.participantid === currentUserParticipant._id)

          if (approver && approver.approved === 'pending') {
            return true
          }

          return false
        })
      } else if (modus === constants.LIST_MODUS.TRASH) {
        return getters.trashedItems
      } else if (modus === constants.LIST_MODUS.LIST ||
         modus === constants.LIST_MODUS.PRINT ||
         modus === constants.LIST_MODUS.EXPORT ||
          modus === constants.LIST_MODUS.MULTI_ACTION ||
          modus === constants.LIST_MODUS.MAILER) {
        return state.list.filter(plan => !plan.trashed)
      } else if (modus === constants.LIST_MODUS.UPDATE || modus === constants.LIST_MODUS.MULTI_ACTION_EDIT) {
        return state.list.filter(plan => {
          return !plan.trashed && rootGetters['acl/hasPermissionToUpdatePlanDoc'](plan)
        })
      }

      return []
    },

    listFiltered: (state, getters, rootState, rootGetters) => {
      return getters.listFilteredByModus.filter(plan => filterFunctions.plans.matchesFilters({
        project: rootState.project.project,
        item: plan,
        ...rootState[rootState.plansListModus.filterStoreName],
        getPfFileById: rootGetters['pfFiles/getById'],
      }))
    },
  },

  mutations: {
    SET_PLANS (state, plans) {
      state.list = plans
    },

    SET_LOADED_PROJECT_ID (state, projectId) {
      state.loadedProjectId = projectId
    },

    ADD_PLAN (state, plan) {
      const existingPlanIndex = state.list.findIndex(p => p._id === plan._id)

      if (existingPlanIndex !== -1) {
        const isUpdateNewer = !state.list[existingPlanIndex].modified || new Date(plan.modified) > new Date(state.list[existingPlanIndex].modified) // Old plans do not have a modified date. We introduce it 2025

        if (isUpdateNewer) {
          state.list[existingPlanIndex] = plan
        }
      } else {
        state.list.push(plan)
      }
    },

    REPLACE_PLAN (state, updatedPlan) {
      const existingPlanIndex = state.list.findIndex(p => p._id === updatedPlan._id)

      if (existingPlanIndex !== -1) {
        const isUpdateNewer = !state.list[existingPlanIndex].modified || new Date(updatedPlan.modified) > new Date(state.list[existingPlanIndex].modified) // Old plans do not have a modified date. We introduce it 2025

        if (isUpdateNewer) {
          state.list[existingPlanIndex] = updatedPlan
        }
      }
    },

    RESET_PLANS (state) {
      state.list = []
      // Kept in sync with `list`: the tab-unmount wipe must also clear the
      // loadedProjectId gate (socket-io TRIGGER_RELOAD_* handlers +
      // refetch-project-data on reconnect both key off this flag), otherwise
      // they'd refetch into a list the user just navigated away from.
      state.loadedProjectId = null
    },

    FULL_RESET (state) {
      state.list = []
      state.loadedProjectId = null
      state.markedItem = null

      state.sortSettings = {
        sortProperty: constants.SORT_PROPERTIES.ITEM_NAME,
        sortOrderReversed: false,
      }

      state.printSettings = {
        groupBy: constants.ITEM_PRINT_SETTINGS.GROUP_BY_NONE,
        showItemUrl: false,
        showComments: false,
      }
    },

    SET_MARKED_ITEM (state, item) {
      state.markedItem = item
    },

    RESET_SORTING (state, item) {
      state.sortSettings.sortProperty = constants.SORT_PROPERTIES.ITEM_NAME
      state.sortSettings.sortOrderReversed = false
    },

    SET_SORT_PROPERTY (state, sortProperty) {
      state.sortSettings.sortProperty = sortProperty
    },

    SET_SORT_ORDER_REVERSED (state, sortOrderReversed) {
      state.sortSettings.sortOrderReversed = sortOrderReversed
    },

    SET_GROUP_BY (state, groupBy) {
      const validModi = [
        constants.ITEM_PRINT_SETTINGS.GROUP_BY_TOP_CATEGORIES,
        constants.ITEM_PRINT_SETTINGS.GROUP_BY_ROLES,
        constants.ITEM_PRINT_SETTINGS.GROUP_BY_PHASETAGS,
        constants.ITEM_PRINT_SETTINGS.GROUP_BY_NONE,
      ]

      if (!validModi.includes(groupBy)) {
        console.error(`${groupBy} is not a valid grouping mode for plans list`)

        return
      }

      state.printSettings.groupBy = groupBy
    },

    SET_SHOW_ITEM_URL (state, newValue) {
      state.printSettings.showItemUrl = newValue
    },

    SET_SHOW_COMMENTS (state, newValue) {
      state.printSettings.showComments = newValue
    },

    SET_SELECTED_MULTI_ACTION_PROPERTY (state, multiActionProperty) {
      state.selectedMultiActionProperty = multiActionProperty
    },
  },

  actions: {
    async fetch ({ commit, rootState }, projectId) {
      if (fetchAbortControllers.fetch instanceof AbortController) {
        fetchAbortControllers.fetch.abort()
      }

      fetchAbortControllers.fetch = new AbortController()

      const plans = await ignoreCanceledRequest(PlansApi.getPlans({
        signal: fetchAbortControllers.fetch.signal,
        axios: this.$axios,
        projectId,
      }))

      // The project was switched while the request was in flight: drop the previous project's list
      if (!plans || rootState.project.requestedProjectId !== projectId) {
        return
      }

      commit('SET_PLANS', plans)
      commit('SET_LOADED_PROJECT_ID', projectId)
    },

    async fetchById ({ commit, rootState }, planId) {
      const projectId = rootState.project.requestedProjectId

      if (fetchAbortControllers.fetchById instanceof AbortController) {
        fetchAbortControllers.fetchById.abort()
      }

      fetchAbortControllers.fetchById = new AbortController()

      const plan = await ignoreCanceledRequest(PlansApi.getPlan({
        signal: fetchAbortControllers.fetchById.signal,
        axios: this.$axios,
        planId,
      }))

      // The project was switched while the request was in flight: drop the previous project's item
      if (!plan || rootState.project.requestedProjectId !== projectId) {
        return
      }

      commit('ADD_PLAN', plan)
    },

    async updatePlanTags ({ commit, dispatch }, { item, newValue }) {
      const updatedPlan = await PlansApi.updateTags({
        axios: this.$axios,
        plan: item,
        newValue,
      })

      await dispatch('project/fetch', item.projectid, { root: true })

      commit('REPLACE_PLAN', updatedPlan)
    },

    async updatePlanPhasetag ({ commit }, { item, newPhasetag }) {
      const updatedPlan = await PlansApi.updatePhasetag({
        axios: this.$axios,
        plan: item,
        newPhasetag,
      })

      commit('REPLACE_PLAN', updatedPlan)
    },

    async updatePlanTopCategory ({ commit }, { item, newTopCategory }) {
      const updatedPlan = await PlansApi.updateTopCategory({
        axios: this.$axios,
        plan: item,
        newTopCategory,
      })

      commit('REPLACE_PLAN', updatedPlan)
    },

    async addPlanRevision ({ commit }, { item, revision }) {
      const updatedPlan = await PlansApi.addRevision({
        axios: this.$axios,
        plan: item,
        revision,
      })

      commit('REPLACE_PLAN', updatedPlan)
    },

    async updatePlanTrashed ({ commit, dispatch }, { item, newTrashedValue }) {
      const updatedPlan = await PlansApi.updateTrashed({
        axios: this.$axios,
        plan: item,
        newTrashedValue,
      })

      commit('REPLACE_PLAN', updatedPlan)
      await dispatch('project/fetch', this.state.project.project._id, { root: true })
    },

    async updateRevisionApprovalStatus ({ commit, dispatch }, { item, approver, approvalStatus, message, pfFileIds }) {
      const updatedPlan = await PlansApi.updateRevisionApprovalStatus({
        axios: this.$axios,
        plan: item,
        approver,
        approvalStatus,
        message,
        pfFileIds,
      })

      commit('REPLACE_PLAN', updatedPlan)
      await dispatch('project/fetch', this.state.project.project._id, { root: true })
    },

    async addRevisionApprovalComment ({ commit, rootState }, { item, approver, message, pfFileIds }) {
      const updatedPlan = await PlansApi.addRevisionApprovalComment({
        axios: this.$axios,
        plan: item,
        approver,
        message,
        pfFileIds,
      })

      commit('REPLACE_PLAN', updatedPlan)
    },

    async updatePlanRevisionApproval ({ commit }, { item, revision, newParticipantIds, newApprovalMode, newApprovalRequestMessage, newApprovalDate }) {
      const updatedPlan = await PlansApi.updateRevisionApproval({
        axios: this.$axios,
        plan: item,
        revision,
        newParticipantIds,
        newApprovalMode,
        newApprovalRequestMessage,
        newApprovalDate,
      })

      commit('REPLACE_PLAN', updatedPlan)
    },

    async approveRevisionByMyself ({ commit }, { item, revision, comment }) {
      const updatedPlan = await PlansApi.approveRevisionByMyself({
        axios: this.$axios,
        plan: item,
        revision,
        comment,
      })

      commit('REPLACE_PLAN', updatedPlan)
    },

    async rejectRevisionByMyself ({ commit }, { item, revision, comment }) {
      const updatedPlan = await PlansApi.rejectRevisionByMyself({
        axios: this.$axios,
        plan: item,
        revision,
        comment,
      })

      commit('REPLACE_PLAN', updatedPlan)
    },

    async updatePlanAuthor ({ commit }, { item, newUserId, newRole }) {
      const updatedPlan = await PlansApi.updateAuthor({
        axios: this.$axios,
        plan: item,
        newUserId,
        newRole,
      })

      commit('REPLACE_PLAN', updatedPlan)
    },

    async updatePlanName ({ commit }, { item, newName, newContent }) {
      const updatedPlan = await PlansApi.updateName({
        axios: this.$axios,
        plan: item,
        newName,
        newContent,
      })

      commit('REPLACE_PLAN', updatedPlan)
    },
  },
}
